import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Hero from '../components/Hero';
import TableOfContents from '../components/TableOfContents';
import WhatAreGreenGasTurbines from '../components/WhatAreGreenGasTurbines';
import Features from '../components/Features';
import HowGreenGasTurbinesWork from '../components/HowGreenGasTurbinesWork';
import EnergySupplyChart from '../components/EnergySupplyChart';
import CO2EmissionsCounter from '../components/CO2EmissionsCounter';
import GreenVsConventional from '../components/GreenVsConventional';
import Statistics from '../components/Statistics';
import About from '../components/About';
import LatestArticles from '../components/LatestArticles';
import LatestTools from '../components/LatestTools';
import ContactForm from '../components/ContactForm';
import FAQ from '../components/FAQ';
import Footer from '../components/Footer';

/**
 * JSON-LD structured data for SEO rich snippets.
 * Organization + WebSite + Article schemas improve SERP appearance and CTR.
 */
const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Green Gas Turbines",
    "alternateName": "GreenGasTurbines",
    "url": "https://www.greengasturbines.com",
    "logo": "https://www.greengasturbines.com/favicon.png",
    "description": "Green Gas Turbines is a leading resource for hydrogen-ready, low-carbon gas turbine technology—covering specs, retrofit guides, interactive engineering tools, and market analysis for the energy transition.",
    "sameAs": [
      "https://www.facebook.com/greengasturbines/",
      "https://x.com/GreenGasTs",
      "https://www.youtube.com/@greengasturbines",
      "https://www.linkedin.com/company/green-gas-turbines"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-813-123-4567",
      "contactType": "sales",
      "availableLanguage": ["English"]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Green Gas Turbines",
    "alternateName": "GreenGasTurbines.com",
    "url": "https://www.greengasturbines.com",
    "description": "The definitive guide to hydrogen-ready gas turbines: technical specs, retrofit economics, interactive calculators, and expert analysis for engineers, operators, and investors.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.greengasturbines.com/blog?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Green Gas Turbines: The Complete Guide to Hydrogen-Ready Low-Carbon Power (2025)",
    "description": "Everything you need to know about green gas turbines—what they are, how they work, fuel pathways (hydrogen, RNG, biogas), efficiency comparisons, retrofit economics, and the role of H₂-ready turbines in the net-zero energy transition.",
    "image": "https://www.greengasturbines.com/green-gas-turbines.jpg",
    "author": {
      "@type": "Organization",
      "name": "Green Gas Turbines",
      "url": "https://www.greengasturbines.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Green Gas Turbines",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.greengasturbines.com/favicon.png"
      }
    },
    "datePublished": "2025-01-15",
    "dateModified": "2025-11-04",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://www.greengasturbines.com/"
    },
    "keywords": "green gas turbines, hydrogen gas turbine, hydrogen-ready turbine, low-carbon power, RNG turbine, biogas turbine, H2 ready, gas turbine retrofit, hydrogen combustion, clean energy turbine"
  }
];

const LandingPage = () => {
  const location = useLocation();

  useEffect(() => {
    // Handle hash navigation when coming from other pages
    if (location.hash) {
      // Wait for page to render, then scroll to section
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // If no hash, scroll to top
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Green Gas Turbines — The Complete Guide to Hydrogen-Ready Low-Carbon Power (2025)</title>
        <meta name="description" content="Everything about green gas turbines: how hydrogen-ready turbines work, fuel options (H₂, RNG, biogas), efficiency data, retrofit costs, and interactive engineering tools. Updated for 2025." />
        <link rel="canonical" href="https://www.greengasturbines.com/" />

        {/* Open Graph */}
        <meta property="og:title" content="Green Gas Turbines — Complete Guide to Hydrogen-Ready Power" />
        <meta property="og:description" content="How green gas turbines cut emissions up to 100% with hydrogen, RNG & biogas. Specs, retrofit guides, calculators & expert analysis for engineers and investors." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.greengasturbines.com/" />
        <meta property="og:image" content="https://www.greengasturbines.com/green-gas-turbines.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Green gas turbine with hydrogen-ready badge" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Green Gas Turbines — Hydrogen-Ready Low-Carbon Power Guide" />
        <meta name="twitter:description" content="How green gas turbines cut emissions up to 100% with hydrogen. Specs, retrofit costs, interactive tools & expert analysis." />
        <meta name="twitter:image" content="https://www.greengasturbines.com/green-gas-turbines.jpg" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Header />
      <Hero />
      <TableOfContents />
      <WhatAreGreenGasTurbines />
      <Features />
      <HowGreenGasTurbinesWork />
      <EnergySupplyChart />
      <CO2EmissionsCounter />
      <GreenVsConventional />
      <Statistics />
      <About />
      <LatestArticles />
      <LatestTools />
      <ContactForm />
      <FAQ />
      <Footer />
    </div>
  );
};

export default LandingPage;